
import { useState, useEffect } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';

interface SuburbComboboxProps {
  onSuburbSelect: (suburb: string) => void;
  selectedSuburbs: string[];
}

export const SuburbCombobox = ({ onSuburbSelect, selectedSuburbs }: SuburbComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [suburbs, setSuburbs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchSuburbs = async () => {
      setLoading(true);
      try {
        // Get unique suburbs from venues table
        const { data, error } = await supabase
          .from('venues')
          .select('suburb')
          .not('suburb', 'is', null)
          .order('suburb');

        if (error) {
          console.error('Error fetching suburbs:', error);
          return;
        }

        // Extract unique suburbs
        const uniqueSuburbs = Array.from(
          new Set(data.map(item => item.suburb).filter(Boolean))
        ).sort();

        setSuburbs(uniqueSuburbs);
      } catch (error) {
        console.error('Error fetching suburbs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSuburbs();
  }, []);

  // Filter suburbs based on search input
  const filteredSuburbs = search === '' 
    ? suburbs
    : suburbs.filter((suburb) => 
        suburb.toLowerCase().includes(search.toLowerCase())
      );

  const handleSelect = (suburb: string) => {
    if (!selectedSuburbs.includes(suburb)) {
      onSuburbSelect(suburb);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selectedSuburbs.length > 0 
            ? `${selectedSuburbs.length} suburb${selectedSuburbs.length > 1 ? 's' : ''} selected` 
            : "Select suburbs"}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          <span className="sr-only">Open suburb selector</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput 
            placeholder="Search suburbs..." 
            className="h-9" 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? "Loading suburbs..." : "No suburbs found."}
            </CommandEmpty>
            <CommandGroup>
              {filteredSuburbs.map((suburb) => (
                <CommandItem
                  key={suburb}
                  value={suburb}
                  onSelect={() => handleSelect(suburb)}
                  className="flex items-center justify-between"
                >
                  {suburb}
                  {selectedSuburbs.includes(suburb) && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
